import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FaBuilding, FaLayerGroup, FaMapMarkerAlt, FaRulerVertical } from 'react-icons/fa';
import { VTOType } from '../../../../Components/VideoComponents/VideoTitleWithOverlay/VideoTitleWithOverlay';
import ProjectShowcasePage, { ProjectShowcaseMetaItem, ProjectShowcaseSlide } from '../Shared/ProjectShowcasePage';
import ovivLogo from '../../../../Assets/images/OVIV/Logos/QuartierOviv_logo_horizontal_beige.png';
import image1 from '../../../../Assets/images/OVIV/06-01.jpeg';
import image2 from '../../../../Assets/images/OVIV/06-02.jpeg';
import image3 from '../../../../Assets/images/OVIV/06-03.jpeg';

export default function Charlemagne() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const isFrench = i18n.language.startsWith('fr');

    const meta: ProjectShowcaseMetaItem[] = [
        {
            label: isFrench ? 'Unités' : 'Units',
            value: String(t('projects.oviv.metadata.total_units')),
            icon: FaBuilding
        },
        {
            label: isFrench ? 'Phases' : 'Phases',
            value: String(t('projects.oviv.metadata.total_phases')),
            icon: FaLayerGroup
        },
        {
            label: isFrench ? 'Gabarit' : 'Height',
            value: t('projects.oviv.metadata.building_heights'),
            icon: FaRulerVertical
        },
        {
            label: isFrench ? 'Lieu' : 'Location',
            value: t('projects.oviv.metadata.location'),
            icon: FaMapMarkerAlt
        }
    ];

    const slides: ProjectShowcaseSlide[] = [
        {
            image: image1,
            eyebrow: t('projects.oviv.paragraph_2.title'),
            title: t('projects.oviv.subtitle'),
            description: t('projects.oviv.paragraph_2.text')
        },
        {
            image: image2,
            eyebrow: t('projects.oviv.phase_1.subtitle'),
            title: t('projects.oviv.phase_1.title'),
            description: t('projects.oviv.phase_1.text')
        },
        {
            image: image3,
            eyebrow: t('projects.oviv.features.title'),
            title: t('projects.oviv.vision.subtitle'),
            description: t('projects.oviv.features.conclusion')
        }
    ];

    const features = [
        t('projects.oviv.features.keypoints.keypoint1'),
        t('projects.oviv.features.keypoints.keypoint2'),
        t('projects.oviv.features.keypoints.keypoint3'),
        t('projects.oviv.features.keypoints.keypoint4'),
        t('projects.oviv.features.keypoints.keypoint5')
    ];

    return (
        <ProjectShowcasePage
            theme="oviv"
            logo={ovivLogo}
            logoAlt="Quartier Oviv"
            heroImage={image1}
            heroType={VTOType.image}
            heroAction={{
                label: isFrench ? 'Visiter le site' : 'View website',
                onClick: () => window.open('https://www.quartieroviv.ca', '_blank')
            }}
            heroCaption={t('projects.oviv.subtitle')}
            introEyebrow={isFrench ? 'Projet résidentiel' : 'Residential project'}
            title={t('projects.oviv.title')}
            subtitle={t('projects.oviv.subtitle')}
            introLead={t('projects.oviv.paragraph_1')}
            introBody={[t('projects.oviv.paragraph_2.text')]}
            meta={meta}
            storyEyebrow={t('projects.oviv.phase_1.subtitle')}
            storyTitle={t('projects.oviv.phase_1.title')}
            storyBody={[
                t('projects.oviv.phase_1.text'),
                t('projects.oviv.features.conclusion')
            ]}
            storyImage={image2}
            storyImageAlt={t('projects.oviv.phase_1.title')}
            storyAction={{
                label: isFrench ? 'Explorer le projet' : 'Explore the project',
                onClick: () => window.open('https://www.quartieroviv.ca/#projet', '_blank')
            }}
            secondaryStoryAction={{
                label: t('projects.oviv.vision.button'),
                onClick: () => navigate('/histoire')
            }}
            galleryEyebrow={isFrench ? 'Parcours visuel' : 'Visual journey'}
            galleryTitle={isFrench ? 'Une lecture plus large du projet' : 'A wider reading of the project'}
            galleryDescription={
                isFrench
                    ? "Les images prennent plus de place pour mieux montrer l’échelle, le bord de l’eau et le caractère du quartier."
                    : 'The imagery is given more room to show the scale, waterfront setting, and character of the district.'
            }
            slides={slides}
            previousSlideLabel={isFrench ? 'Image précédente' : 'Previous image'}
            nextSlideLabel={isFrench ? 'Image suivante' : 'Next image'}
            goToSlideLabel={isFrench ? "Aller à l'image" : 'Go to image'}
            featuresEyebrow={isFrench ? 'Durabilité' : 'Sustainability'}
            featuresTitle={t('projects.oviv.features.title')}
            featuresLead={
                isFrench
                    ? "Les choix techniques et environnementaux du projet soutiennent directement le confort quotidien des résidents."
                    : 'The project’s technical and environmental choices directly support everyday resident comfort.'
            }
            features={features}
            featuresConclusion={t('projects.oviv.features.conclusion')}
            asideEyebrow={isFrench ? 'Vision SIMDEV' : 'SIMDEV vision'}
            asideTitle={t('projects.oviv.vision.title')}
            asideSubtitle={t('projects.oviv.vision.subtitle')}
            asideBody={t('projects.oviv.vision.text')}
            asideAction={{
                label: t('projects.oviv.vision.button'),
                onClick: () => navigate('/histoire')
            }}
        />
    );
}
